// services/firebaseFunctions.ts
//
// Lazy bootstrap for Firebase Functions callables. The firebase/
// functions SDK is ~80 KB and was previously pulled into the entry
// chunk via getFunctions() in config/firebase.ts. Splitting it out
// behind a dynamic import keeps it off main.js — it now arrives
// only when the first callable runs (registration flows, child-
// account flows, settings deletes, etc.).
//
// Usage:
//   const checkPlayerId = await getCallable<{ playerId: string },
//     { available: boolean; reason: string | null }>(
//     'checkPlayerIdAvailable',
//   );
//   const { data } = await checkPlayerId({ playerId });
//
// The first call dynamic-imports firebase/functions, initialises
// the Functions instance (Europe-West 1, matching where our Cloud
// Functions are deployed), and caches both. Every subsequent call
// reuses the cached instance — no repeat dynamic imports.

import type { Functions, HttpsCallable, HttpsCallableOptions } from 'firebase/functions';
import { app } from '../config/firebase';

let functionsPromise: Promise<Functions> | null = null;

async function ensureFunctions(): Promise<Functions> {
  if (!functionsPromise) {
    functionsPromise = import('firebase/functions').then(({ getFunctions }) =>
      getFunctions(app, 'europe-west1'),
    );
  }
  return functionsPromise;
}

export async function getCallable<I = unknown, O = unknown>(
  name: string,
  options?: HttpsCallableOptions,
): Promise<HttpsCallable<I, O>> {
  const [functions, { httpsCallable }] = await Promise.all([
    ensureFunctions(),
    import('firebase/functions'),
  ]);
  return httpsCallable<I, O>(functions, name, options);
}
